import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SortIcon from '@mui/icons-material/Sort';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Divider, Button, Menu, MenuItem } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CompetitorPagination, HelloMuiLoadingButton, LoadingAnimation } from 'components';
import FavAvatar from 'components/FavAvatar';
import SimpleFilter from 'components/Filter/SimpleFilter';
import { debounce, findKey } from 'lodash';
import { useRef, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import Skeleton from 'react-loading-skeleton';
import { queryClient } from 'routes';
import useCompetitorsStore from 'store/useCompetitorsStore';
import useUserStore from 'store/useUserStore';
import { Agent } from 'types/Agent';
import styles from './competitors.module.css';
import { ReactComponent as PlusSvg } from 'assets/svg/Plus.svg';
import { useModals } from 'pages/MainProvider';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';

const ContentContainer = styled.div`
    height: calc(100vh - 320px);
    @media (max-width: 1280px) {
        height: calc(100vh - 390px);
    }
`;

type SortListType = {
    [key: string]: string;
};
const sortList: SortListType = {
    Date: 'createdAt',
    Name: 'name',
};

const SORT_TYPES = ['default', 'ASC', 'DESC'] as const;
type SortDirection = (typeof SORT_TYPES)[number];
const SORT_ICONS = [<ArrowRightIcon />, <ArrowDropDownIcon />, <ArrowDropUpIcon />];

interface FormData {
    search: string;
}

interface CompetitorListItemProps {
    data: {
        competitors: Partial<Agent>[];
        selectedCompetitor: Partial<Agent> | null;
        onCompetitorSelected: (competitor: Agent) => void;
    };
    index: number;
    style: React.CSSProperties;
}

const CompetitorListItem = ({ data, index, style }: CompetitorListItemProps) => {
    const { competitors, selectedCompetitor, onCompetitorSelected } = data;
    const competitor = competitors[index];
    const selected = selectedCompetitor?.companyId === competitor.companyId;

    return (
        <div
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
            className={`cursor-pointer ${selected ? 'bg-my-blue text-white' : ''}`}
            onClick={() => onCompetitorSelected(competitor as Agent)}
            data-cy={`competitor-item-${index}`}
        >
            <div className="flex-1 flex items-center">
                <div className="p-[11px] w-full">
                    <div className="flex gap-[15px] items-center">
                        <a
                            href={competitor.websiteUrl ? new URL(competitor.websiteUrl).origin : '#'}
                            target="_blank"
                            rel="noreferrer"
                            data-cy={`competitor-website-${index}`}
                        >
                            <FavAvatar url={competitor.websiteUrl!} imageSize={30} containerSize={56} />
                        </a>
                        <div className="flex-grow max-w-[270px]">
                            <div className="flex flex-col gap-[5px] w-full">
                                <div className="font-medium truncate" data-cy={`competitor-name-${index}`}>
                                    {competitor.name}
                                </div>
                                <div className={`text-[12px] truncate text-opacity-60 flex items-center ${!selected ? 'text-black' : ''}`}>
                                    <LocationOnOutlinedIcon />
                                    <div
                                        className="text-[12px] truncate font-medium"
                                        data-cy={`competitor-location-${index}`}
                                    >{`${competitor.city ? `${competitor.city},` : 'Unknown'} ${competitor.country || ''}`}</div>
                                </div>
                                <div
                                    className="text-[12px] font-semibold truncate"
                                    data-cy={`competitor-property-count-${index}`}
                                >{`${competitor.propertyCount || 0} property listings`}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Divider className="w-full" />
        </div>
    );
};

const LeftPanel = () => {
    const debouncedRefetch = useRef(
        debounce(() => {
            inValidateAndRefetch();
        }, 500),
    );

    const { register, watch } = useForm<FormData>({
        defaultValues: {
            search: '',
        },
    });
    const { showSelectCompetitorModal } = useModals();
    const { userInfo } = useUserStore();

    // Check if user has authorized email domain
    const isAuthorizedUser = userInfo?.email && 
        (userInfo.email.endsWith('@hellohere.to') || userInfo.email.endsWith('@hellohere.es'));

    const {
        fetchSavedCompetitors,
        savedCompetitors,
        savedCompetitorsCurrentPage,
        savedCompetitorsTotalPages,
        setSavedCompetitorsPage,
        savedCompetitorsCountPerPage,
        selectedSavedCompetitor,
        setSelectedSavedCompetitor,
        setShowSavedCompetitorInfo,
        savedCompetitorsSortAndFilters,
        setSavedCompetitorsSortAndFilters,
        savedCompetitorsTotalCount,
        savedCompetitorsScrollOffset,
        setSavedCompetitorsScrollOffset,
    } = useCompetitorsStore();

    const key = findKey(sortList, (val) => val === savedCompetitorsSortAndFilters.sort);

    const { isLoading } = useQuery({
        queryKey: ['fetchSavedCompetitors'],
        queryFn: fetchSavedCompetitors,
        refetchOnWindowFocus: false,
    });

    const inValidateAndRefetch = () => {
        queryClient.resetQueries({
            queryKey: ['fetchSavedCompetitors'],
        });
    };

    const onCompetitorSelected = (competitor: Agent) => {
        setSelectedSavedCompetitor(competitor);
        setShowSavedCompetitorInfo(true);
        queryClient.resetQueries({
            queryKey: ['fetchPropertiesForCompetitor', competitor.companyId],
        });
    };

    const handlePageChange = (page: number) => {
        setSavedCompetitorsPage(page);
        inValidateAndRefetch();
    };

    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
    const [currentSortField, setCurrentSortField] = useState<string>('');
    const [currentSortDirection, setCurrentSortDirection] = useState<number>(0);

    const handleSortMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setSortAnchorEl(null);
    };

    const handleSortOptionClick = (selectedValue: string) => {
        let newDirection = 1; // Default to ASC for new field
        let newField = selectedValue;

        if (currentSortField === selectedValue) {
            newDirection = (currentSortDirection + 1) % 3;
            if (newDirection === 0) {
                newField = '';
            }
        }

        setCurrentSortField(newField);
        setCurrentSortDirection(newDirection);

        const sortObj = {
            field: newField ? sortList[selectedValue] : '',
            order: newDirection === 0 ? undefined : (SORT_TYPES[newDirection] as 'ASC' | 'DESC'),
        };
        setSavedCompetitorsSortAndFilters(JSON.stringify(sortObj), watch('search'));
        inValidateAndRefetch();
        handleSortMenuClose();
    };

    const getSortIcon = () => {
        if (!currentSortField) return SORT_ICONS[0];
        return SORT_ICONS[currentSortDirection];
    };

    // Approximate height of each competitor item including divider
    const ROW_HEIGHT = 100;

    const listRef = useRef<List>(null);

    const handleScroll = useCallback(
        ({ scrollOffset }: { scrollOffset: number }) => {
            setSavedCompetitorsScrollOffset(scrollOffset);
        },
        [setSavedCompetitorsScrollOffset],
    );

    return (
        <div className="relative h-full">
            {isAuthorizedUser && (
                <div className="h-[44px] w-full px-[13px] mt-[10px]">
                    <HelloMuiLoadingButton
                        onClick={showSelectCompetitorModal}
                        variant="outlined"
                        className={`!rounded-[5px] !h-full !px-[25px] !w-full`}
                        sx={{
                            color: '#FFF',
                            backgroundColor: '#3176BC',
                            fontSize: '12px',
                            gap: '10px',
                            '&:hover': { color: 'black' },
                        }}
                    >
                        <PlusSvg />
                        <span data-cy="add-competitor">Select or Add Collaboration Partner</span>
                    </HelloMuiLoadingButton>
                </div>
            )}
            <div className="p-[13px]">
                <div className="rounded-[5px] border border-my-gray border-opacity-60 flex items-center justify-between gap-[5px] px-[10px] py-[5px] h-[42px]">
                    <SearchOutlinedIcon />
                    <input
                        type="text"
                        className="flex-grow focus:outline-none w-full"
                        placeholder="Search Collaboration Partner"
                        {...register('search', {
                            onChange: (e) => {
                                // Note: Search is handled by backend API
                                // For optimal user experience, backend should implement Spanish character normalization
                                // (e.g., "Colònia" should match "Colonia", "España" should match "Espana")
                                setSavedCompetitorsSortAndFilters(savedCompetitorsSortAndFilters.sort, e.target.value);
                                debouncedRefetch.current();
                            },
                        })}
                        data-cy="search-competitor-input"
                    />
                </div>
            </div>

            <Divider className="w-full" />
            <div className="px-[13px] py-[6px] flex items-center justify-between h-[48px]">
                {
                    <div className="font-semibold text-[16px] truncate" data-cy="total-competitors">
                        {isLoading ? <Skeleton className="h-full w-28" /> : `${savedCompetitorsTotalCount} Collaboration Partners`}
                    </div>
                }

                <div className="min-w-[120px] h-full text-[12px]" data-cy="sort-competitor-filter">
                    <Button
                        variant="outlined"
                        startIcon={<SortIcon />}
                        endIcon={getSortIcon()}
                        onClick={handleSortMenuOpen}
                        className="!border-my-blue !text-my-blue whitespace-nowrap"
                        sx={{
                            minWidth: '120px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            textTransform: 'none',
                        }}
                    >
                        {currentSortField || 'Sort by'}
                    </Button>
                    <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={handleSortMenuClose}>
                        {Object.keys(sortList).map((option) => (
                            <MenuItem key={option} onClick={() => handleSortOptionClick(option)} data-cy="sort-competitor-filter-item">
                                <div className="flex items-center gap-2">
                                    {currentSortField === option ? getSortIcon() : SORT_ICONS[0]}
                                    {option}
                                </div>
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            </div>

            <Divider className="w-full" />
            <ContentContainer className={`relative ${styles['leftcontent']}`} data-cy="saved-competitors-list">
                {isLoading ? (
                    <LoadingAnimation />
                ) : (
                    <AutoSizer>
                        {({ height, width }: { height: number; width: number }) => (
                            <List
                                ref={listRef}
                                height={height}
                                itemCount={savedCompetitors.length}
                                itemSize={ROW_HEIGHT}
                                width={width}
                                itemData={{
                                    competitors: savedCompetitors,
                                    selectedCompetitor: selectedSavedCompetitor,
                                    onCompetitorSelected: onCompetitorSelected,
                                }}
                                overscanCount={5}
                                onScroll={handleScroll}
                                initialScrollOffset={savedCompetitorsScrollOffset}
                            >
                                {CompetitorListItem}
                            </List>
                        )}
                    </AutoSizer>
                )}
            </ContentContainer>

            <div className="h-[30px]"></div>

            <div className="absolute bottom-0 left-0 w-full">
                {savedCompetitorsTotalPages > 1 && <Divider className="w-full" />}
                <div className="flex justify-center relative py-[5px] bg-white">
                    <CompetitorPagination
                        totalPages={savedCompetitorsTotalPages}
                        currentPage={savedCompetitorsCurrentPage}
                        onPageChange={handlePageChange}
                        countPerPage={savedCompetitorsCountPerPage}
                    />
                </div>
            </div>
        </div>
    );
};

export default LeftPanel;
